import { useCallback, useEffect, useState } from 'react';
import { Download, Megaphone, Trophy } from 'lucide-react';
import { toast } from 'sonner';
import PortalHeader from '../components/PortalHeader';
import { engageAPI } from '../lib/api';
import { t } from '../lib/i18n';
import { EmptyState, inputCls, panelCls, StatusBadge } from '../components/ui';

export default function CommunityPage() {
  const [anns, setAnns] = useState([]);
  const [assets, setAssets] = useState([]);
  const [contests, setContests] = useState([]);
  const [bonuses, setBonuses] = useState([]);
  const [lb, setLb] = useState(null);
  const [metric, setMetric] = useState('points');
  const [period, setPeriod] = useState('30d');

  const load = useCallback(() => {
    engageAPI.announcements().then(({ data }) => setAnns(data.items)).catch(() => {});
    engageAPI.assets().then(({ data }) => setAssets(data.items)).catch(() => {});
    engageAPI.contests().then(({ data }) => setContests(data.items)).catch(() => {});
    engageAPI.myBonuses().then(({ data }) => setBonuses(data.items)).catch(() => {});
  }, []);
  useEffect(load, [load]);
  useEffect(() => {
    engageAPI.leaderboard(metric, period).then(({ data }) => setLb(data))
      .catch(() => toast.error(t('Could not load the leaderboard')));
  }, [metric, period]);

  return (
    <div className="mx-auto max-w-4xl space-y-6 p-4 sm:p-6">
      <PortalHeader title={t('Community')} />

      {anns.length > 0 && (
        <div className={`${panelCls} p-5`}>
          <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold"><Megaphone size={15} /> {t('Announcements')}</h2>
          <ul className="space-y-2 text-sm">
            {anns.map((a) => (
              <li key={a.id}>
                <span className="font-medium">{a.title}</span>
                {a.body ? <span className="text-mms-muted"> — {a.body}</span> : null}
              </li>
            ))}
          </ul>
        </div>
      )}

      {contests.length > 0 && (
        <div className={`${panelCls} p-5`}>
          <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold"><Trophy size={15} /> {t('Contests')}</h2>
          <div className="space-y-4">
            {contests.map((c) => (
              <div key={c.id}>
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="font-medium">{c.title}</span>
                  <code className="text-xs text-mms-muted">{t(c.metric)}</code>
                  <span className="text-xs text-mms-faint">{(c.starts_at || '').slice(0, 10)} → {(c.ends_at || '').slice(0, 10)}</span>
                  <StatusBadge status={c.status === 'finished' ? 'settled' : 'active'} />
                </div>
                {(c.status === 'finished' ? c.winners : c.standings)?.length > 0 && (
                  <ol className="mt-2 space-y-1 text-sm">
                    {(c.status === 'finished' ? c.winners : c.standings).slice(0, 5).map((row, i) => (
                      <li key={i} className="flex justify-between">
                        <span>{i + 1}. {row.member?.name || '—'}{c.status === 'finished' && row.prize ? ` — ${row.prize}` : ''}</span>
                        <span className="mms-num text-mms-muted">{row.value}</span>
                      </li>
                    ))}
                  </ol>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {bonuses.length > 0 && (
        <div className={`${panelCls} p-5`}>
          <h2 className="mb-3 text-sm font-semibold">{t('Bonus progress')}</h2>
          <div className="grid gap-3 sm:grid-cols-2">
            {bonuses.map((b) => (
              <div key={b.id} className="rounded-md border border-mms-border p-3">
                <div className="flex justify-between text-sm">
                  <span className="font-medium">{b.name}</span>
                  <span className="text-xs text-mms-muted">{b.award?.amount} {t(b.award?.currency)}</span>
                </div>
                <div className="mt-2 h-2 overflow-hidden rounded-full bg-mms-raised">
                  <div className={`h-full rounded-full ${b.earned ? 'bg-[var(--mms-success)]' : 'bg-mms-accent'}`}
                    style={{ width: `${Math.round(b.progress * 100)}%` }} />
                </div>
                <div className="mt-1 text-xs text-mms-faint mms-num">
                  {b.value} / {b.threshold} {t(b.metric)}{b.earned ? ` · ${t('earned')}` : ''}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className={`${panelCls} p-5`}>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-sm font-semibold">{t('Leaderboard')}</h2>
          <div className="flex gap-2">
            <select className={`${inputCls} !w-36`} value={metric} onChange={(e) => setMetric(e.target.value)}>
              {['points', 'xp', 'referrals', 'paid_referrals', 'clicks', 'revenue'].map((m) => <option key={m} value={m}>{t(m)}</option>)}
            </select>
            <select className={`${inputCls} !w-24`} value={period} onChange={(e) => setPeriod(e.target.value)}>
              {['7d', '30d', '90d', 'all'].map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
        </div>
        {!lb || lb.rows.length === 0 ? (
          <EmptyState>{t('No activity for this metric yet.')}</EmptyState>
        ) : (
          <ol className="space-y-1.5 text-sm">
            {lb.rows.map((r, i) => (
              <li key={i} className="flex justify-between">
                <span>{i + 1}. {r.member?.name || '—'}</span>
                <span className="mms-num text-mms-muted">{r.value}</span>
              </li>
            ))}
          </ol>
        )}
      </div>

      {assets.length > 0 && (
        <div className={`${panelCls} p-5`}>
          <h2 className="mb-3 text-sm font-semibold">{t('Marketing assets')}</h2>
          <ul className="space-y-1.5 text-sm">
            {assets.map((a) => (
              <li key={a.id} className="flex items-center gap-2">
                <Download size={13} className="text-mms-muted" />
                <a href={a.url} target="_blank" rel="noreferrer" className="text-mms-accent underline">{a.title}</a>
                <span className="text-xs text-mms-faint">{(a.size / 1024).toFixed(0)} KB</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
